import { Fragment } from "react";
import { Field, FieldArray, getIn, useFormikContext } from "formik";

import Input from "Components/Form/Input";
import PanelContent from "Components/Panels/PanelContent";
import { MaskCEP } from "Utils/Masks";
import { useUnit } from "Pages/Units/useUnit";

const PARKING_ITEM_FIELDS = [
  "parking",
  "addresses.cep",
  "distance",
  "addresses.number",
  "addresses.street",
];

function isParkingItemComplete(item) {
  if (!item || item.deleted) return false;

  return PARKING_ITEM_FIELDS.every((field) => {
    const value = getIn(item, field);
    return value !== undefined && value !== null && String(value).trim() !== "";
  });
}

function getLastActiveParkingIndex(parkingInfo = []) {
  let lastIndex = -1;

  parkingInfo.forEach((item, index) => {
    if (!item?.deleted) lastIndex = index;
  });

  return lastIndex;
}

function createParkingItem(cityId) {
  return {
    parking: "",
    distance: "",
    addresses: {
      cep: "",
      number: "",
      street: "",
      city_id: cityId,
    },
  };
}

async function validateParkingItem(index, setFieldTouched, validateField) {
  await Promise.all(
    PARKING_ITEM_FIELDS.map(async (field) => {
      const path = `parkings.${index}.${field}`;
      setFieldTouched(path, true, false);
      await validateField(path);
    })
  );
}

export function Parkings() {
  const { values, setFieldValue, errors, setFieldTouched, validateField } =
    useFormikContext();

  const { handleDeleteParking } = useUnit(null);

  const parkingInfo = values.parkings ?? [];
  const hasParkingsActive = values.parking;

  function getFieldError(index, field) {
    return getIn(errors, `parkings.${index}.${field}`) ?? "";
  }

  const handleParkingChange = async (event, index, field) => {
    const val = event.target.value;
    setFieldValue(`parkings.${index}.${field}`, val);

    if (getIn(errors, `parkings.${index}.${field}`)) {
      await validateField(`parkings.${index}.${field}`);
    }
  };

  return (
    <PanelContent title="Estacionamentos Próximo">
      <section
        style={{
          display: "flex",
          flexWrap: "wrap",
          borderBottom: "1px dashed #E5E5E5",
          marginBottom: "2rem",
        }}
      >
        <FieldArray
          name="parkings"
          render={({ push, remove }) => {
            const addNewInfo = async () => {
              setFieldValue("parking", true);

              const lastIndex = getLastActiveParkingIndex(parkingInfo);

              if (lastIndex === -1) {
                push(createParkingItem(values.addresses.city_id));
                return;
              }

              if (!isParkingItemComplete(parkingInfo[lastIndex])) {
                await validateParkingItem(
                  lastIndex,
                  setFieldTouched,
                  validateField
                );
                return;
              }

              push(createParkingItem(values.addresses.city_id));
            };

            const removeItem = (index, itemId) => {
              if (itemId) {
                handleDeleteParking(itemId);
              }
              remove(index);
            };

            return (
              <>
                {hasParkingsActive &&
                  parkingInfo.map((parking, index) => {
                    if (parking.deleted) return null;

                    return (
                      <Fragment key={`parking-info-${index}`}>
                        <div className="sm-12 md-6">
                          <Field
                            component={Input}
                            name={`parkings.${index}.parking`}
                            id={`parkings.${index}.parking`}
                            label="Nome Estacionamento"
                            type="text"
                            maxLength={245}
                            value={parkingInfo[index]?.parking ?? ""}
                            error={getFieldError(index, "parking")}
                            onChange={(e) =>
                              handleParkingChange(e, index, "parking")
                            }
                          />
                        </div>

                        <div className="sm-12 md-6">
                          <Field
                            component={Input}
                            name={`parkings.${index}.addresses.cep`}
                            id={`parkings.${index}.addresses.cep`}
                            label="Cep"
                            type="text"
                            placeholder={MaskCEP("00000000")}
                            maxLength="9"
                            value={MaskCEP(parkingInfo[index]?.addresses?.cep)}
                            error={getFieldError(index, "addresses.cep")}
                            onChange={(e) =>
                              handleParkingChange(e, index, "addresses.cep")
                            }
                          />
                        </div>

                        <div className="sm-12 md-6">
                          <Field
                            component={Input}
                            name={`parkings.${index}.distance`}
                            id={`parkings.${index}.distance`}
                            label="Distância (Metros)"
                            type="text"
                            maxLength={5}
                            placeholder="Digite à distância em metros"
                            value={parkingInfo[index]?.distance ?? ""}
                            error={getFieldError(index, "distance")}
                            onChange={(e) =>
                              handleParkingChange(e, index, "distance")
                            }
                          />
                        </div>

                        <div className="sm-12 md-6">
                          <Field
                            component={Input}
                            name={`parkings.${index}.addresses.number`}
                            label="Numero"
                            type="text"
                            id={`parkings.${index}.addresses.number`}
                            maxLength={5}
                            value={parkingInfo[index]?.addresses?.number ?? ""}
                            error={getFieldError(index, "addresses.number")}
                            onChange={(e) =>
                              handleParkingChange(e, index, "addresses.number")
                            }
                          />
                        </div>

                        <div className="xs-12">
                          <Field
                            component={Input}
                            name={`parkings.${index}.addresses.street`}
                            label="Rua"
                            type="text"
                            id={`parkings.${index}.addresses.street`}
                            maxLength={255}
                            value={parkingInfo[index]?.addresses?.street ?? ""}
                            error={getFieldError(index, "addresses.street")}
                            onChange={(e) =>
                              handleParkingChange(e, index, "addresses.street")
                            }
                          />
                        </div>

                        <div className="xs-12 parking-itens-flex">
                          <div className="parking-grid-buttons">
                            <button
                              type="button"
                              onClick={() => removeItem(index, parking.id)}
                              className="link link-danger"
                            >
                              Excluir
                            </button>
                          </div>
                        </div>
                      </Fragment>
                    );
                  })}

                <div
                  className="xs-12"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <button
                    type="button"
                    className="button _white _icon-add"
                    style={{ marginBottom: "1rem" }}
                    onClick={addNewInfo}
                  >
                    <span className="represent-icon-holder">+</span>
                    Adicionar novo
                  </button>
                </div>
              </>
            );
          }}
        />
      </section>
    </PanelContent>
  );
}
